import { useId } from 'react';

/** Decorative architecture, never a representation of company or project records. */
export function SetupBlueprint({ compact = false }: { compact?: boolean }) {
  const sceneId = useId().replaceAll(':', '');
  const paint = (name: string) => `url(#${sceneId}-${name})`;
  return (
    <div
      className={compact ? 'setup-blueprint setup-blueprint--compact' : 'setup-blueprint'}
      aria-hidden="true"
    >
      <svg viewBox="0 0 760 440" fill="none" focusable="false">
        <defs>
          <linearGradient
            id={`${sceneId}-edge`}
            x1="230"
            y1="90"
            x2="560"
            y2="400"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#e9dfca" />
            <stop offset=".45" stopColor="#a59a83" />
            <stop offset="1" stopColor="#676767" stopOpacity=".25" />
          </linearGradient>
          <linearGradient
            id={`${sceneId}-left`}
            x1="275"
            y1="130"
            x2="365"
            y2="390"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#242424" />
            <stop offset=".5" stopColor="#111111" />
            <stop offset="1" stopColor="#060606" />
          </linearGradient>
          <linearGradient
            id={`${sceneId}-right`}
            x1="495"
            y1="116"
            x2="365"
            y2="390"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#303030" />
            <stop offset=".4" stopColor="#141414" />
            <stop offset="1" stopColor="#090909" />
          </linearGradient>
          <linearGradient
            id={`${sceneId}-roof`}
            x1="300"
            y1="90"
            x2="420"
            y2="170"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#383838" />
            <stop offset="1" stopColor="#0c0c0c" />
          </linearGradient>
          <radialGradient id={`${sceneId}-ground`}>
            <stop stopColor="#fff" stopOpacity=".08" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="390" cy="358" rx="280" ry="76" fill={paint('ground')} />
        <g className="setup-blueprint-ground" stroke="#b0b0b0" strokeOpacity=".15" strokeWidth=".7">
          {[0, 1, 2, 3, 4, 5, 6].map((n) => (
            <path
              key={n}
              d={`M ${20 + n * 75} ${320 - n * 17} L ${365 + n * 52} ${430 - n * 14} M ${160 + n * 70} 238 L ${n * 65} 430`}
            />
          ))}
          <path d="M30 350 350 250 740 365M65 380 390 277 700 375" />
        </g>
        <g
          className="setup-blueprint-masses"
          stroke="#ffffff"
          strokeOpacity=".08"
          strokeWidth=".8"
          strokeLinejoin="round"
        >
          <g className="setup-blueprint-building setup-blueprint-building--one">
            <path d="M142 246 204 268 204 380 142 358Z" fill={paint('left')} />
            <path d="M204 268 300 234 300 346 204 380Z" fill={paint('right')} />
            <path d="M142 246 238 212 300 234 204 268Z" fill={paint('roof')} />
          </g>
          <g className="setup-blueprint-building setup-blueprint-building--two">
            <path d="M275 130 365 162 365 390 275 358Z" fill={paint('left')} />
            <path d="M365 162 495 116 495 344 365 390Z" fill={paint('right')} />
            <path d="M275 130 405 84 495 116 365 162Z" fill={paint('roof')} />
          </g>
          <g className="setup-blueprint-building setup-blueprint-building--three">
            <path d="M495 260 551 280 551 390 495 370Z" fill={paint('left')} />
            <path d="M551 280 635 250 635 360 551 390Z" fill={paint('right')} />
            <path d="M495 260 579 230 635 250 551 280Z" fill={paint('roof')} />
          </g>
        </g>
        <g
          className="setup-blueprint-structure"
          stroke={paint('edge')}
          strokeWidth="1"
          strokeLinejoin="round"
        >
          <path d="M275 358V130L405 84 495 116V344L365 390 275 358M275 130 365 162 495 116M365 162V390M405 84 365 162" />
          <path d="M142 358V246L238 212 300 234V346L204 380 142 358M142 246 204 268 300 234M204 268V380" />
          <path d="M495 370V260L579 230 635 250V360L551 390 495 370M495 260 551 280 635 250M551 280V390" />
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <path
              key={n}
              d={`M275 ${130 + n * 28} 365 ${162 + n * 28} 495 ${116 + n * 28}`}
              opacity=".35"
            />
          ))}
          {[1, 2, 3, 4].map((n) => (
            <path
              key={n}
              d={`M${275 + n * 18} ${130 + n * 6.4}v228M${365 + n * 26} ${162 - n * 9.2}v228M142 ${246 + n * 22} 204 ${268 + n * 22} 300 ${234 + n * 22}M495 ${260 + n * 22} 551 ${280 + n * 22} 635 ${250 + n * 22}`}
              opacity=".3"
            />
          ))}
        </g>
        <path
          className="setup-blueprint-trace"
          d="M275 358V130L405 84 495 116 365 162V390L495 344"
          stroke="#e6d1a5"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <g
          className="setup-blueprint-windows"
          stroke="#e0d8c7"
          strokeWidth="2.1"
          strokeOpacity=".65"
        >
          <path d="m380 207 13-4.6m26-9.2 13-4.6m26 46.8 13-4.6m-91 60.2 13-4.6m26 18.8 13-4.6m-133-57.8 12 4.2m18 34.4 12 4.2" />
        </g>
        <g className="setup-blueprint-crane" stroke="#baa77d" strokeOpacity=".6" strokeWidth="1">
          <path d="M566 239V53h11v185M535 53h135l-94-27-41 27Zm42-27V14m-11 39 11 16-11 16 11 16-11 16 11 16-11 16 11 16-11 16 11 16-11 16 11 16-11 16m11-218 66 27 27-27m-27 0v111m-5 0h10l-5 8-5-8Z" />
        </g>
        <g
          className="setup-blueprint-dimensions"
          stroke="#b3b3b3"
          strokeOpacity=".3"
          strokeWidth=".7"
        >
          <path d="M255 130V358m-5-228h10m-10 228h10M275 421H495m-220-5v10m220-10v10M405 65V30m0 10h81" />
          <circle cx="405" cy="84" r="3" fill="#d8c398" stroke="none" />
          <circle cx="365" cy="390" r="3" fill="#d8c398" stroke="none" />
        </g>
      </svg>
      <span className="setup-blueprint-caption">THE FOUNDATION OF YOUR WORKSPACE</span>
    </div>
  );
}
